import React, { useState } from 'react';
import { Pressable, StyleSheet, ScrollView } from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import useValidateCoupon from '@/Hooks/RenewalApi/useValidateCoupon';
import useGetAvailableCoupons from '@/Hooks/RenewalApi/useGetAvailableCoupons';
import CCLoader from '@/Atoms/CCLoader';

interface CouponModalProps {
  visible: boolean;
  onClose: () => void;
  onApplyCoupon: (coupon: any) => void;
  tournamentId?: number;
  planType?: string;
  durationMonths?: number;
  appliedCouponCode?: string;
}

const CouponModal: React.FC<CouponModalProps> = ({
  visible,
  onClose,
  onApplyCoupon,
  tournamentId,
  planType,
  durationMonths,
  appliedCouponCode,
}) => {
  const [couponCode, setCouponCode] = useState<string>('');
  const [error, setError] = useState<string>('');

  const { data: availableCoupons, isLoading: isLoadingCoupons } = useGetAvailableCoupons(
    {
      tournament_id: tournamentId,
      plan_type: planType,
      duration_months: durationMonths,
    },
    visible,
  );

  const { mutate: validateCoupon, isLoading: isValidating } = useValidateCoupon(
    (data) => {
      if (data.valid && data.coupon) {
        setError('');
        onApplyCoupon(data.coupon);
        onClose();
      } else {
        setError(data.error || 'Invalid coupon code');
      }
    },
    (error: any) => {
      setError(error?.response?.data?.error || error?.message || 'Failed to validate coupon');
    },
  );

  const handleApplyCoupon = (coupon?: any) => {
    const codeToValidate = coupon?.code || couponCode.trim();
    if (!codeToValidate) {
      setError('Please enter a coupon code');
      return;
    }

    setError('');
    validateCoupon({
      coupon_code: codeToValidate,
      tournament_id: tournamentId,
      plan_type: planType,
      duration_months: durationMonths,
    });
  };

  const handleSelectCoupon = (coupon: any) => {
    setCouponCode(coupon.code);
    handleApplyCoupon(coupon);
  };

  return (
    <SlideModal
      visible={visible}
      showHeader
      showCloseIcon
      onClose={onClose}
      type="side"
    //   type="center"
      // customStyle={{
      //   minWidth: 500,
      //   width: 500,
      //   minHeight: 400,
      // }}
      modalHeaderTitle={
        <Box type="row-center" gap={3}>
          <MaterialCommunityIcons name="ticket-percent" size={18} color={CCColors.TextColor.Dark} />
          <CCText fontSize={FontSizes[16]} fontWeight="semibold">
            Apply Coupon
          </CCText>
        </Box>
      }>
      <Box padding={20}>
        {/* Manual Coupon Input */}
        <Box marginBottom={20}>
          <CCText fontSize={FontSizes[14]} fontWeight="semibold" marginBottom={10} color={CCColors.TextColor.Dark}>
            Enter Coupon Code
          </CCText>
          <Box flexDirection="row" gap={10}>
            <Box flex={1}>
              <CCTextInput
                value={couponCode}
                onChangeText={(text) => {
                  setCouponCode(text.toUpperCase());
                  setError('');
                }}
                placeholder="Enter coupon code"
                style={styles.input}
              />
            </Box>
            <CCButton
              onPress={() => handleApplyCoupon()}
              disabled={isValidating || !couponCode.trim()}
              style={styles.applyButton}
              contentContainerStyle={styles.buttonContent}>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
                Apply
              </CCText>
            </CCButton>
          </Box>
          {error && (
            <Box marginTop={8}>
              <CCText fontSize={FontSizes[12]} color={CCColors.Red}>
                {error}
              </CCText>
            </Box>
          )}
        </Box>

        {/* Available Coupons List */}
        <Box marginBottom={20}>
          <CCText fontSize={FontSizes[14]} fontWeight="semibold" marginBottom={10} color={CCColors.TextColor.Dark}>
            Available Coupons
          </CCText>
          {isLoadingCoupons ? (
            <Box padding={20} alignItems="center">
              <CCLoader isComponentLoader loading={true} />
            </Box>
          ) : availableCoupons?.coupons && availableCoupons.coupons.length > 0 ? (
            <ScrollView >
              <Box gap={10}>
                {availableCoupons.coupons.map((coupon, index) => {
                  const isApplied = appliedCouponCode === coupon.code;
                  return (
                    <Pressable
                      key={index}
                      onPress={() => !isApplied && handleSelectCoupon(coupon)}
                      disabled={isApplied}
                      style={[
                        styles.couponCard,
                        isApplied && styles.couponCardApplied,
                      ]}>
                      <Box flexDirection="row" alignItems="center" justifyContent="space-between">
                        <Box flex={1}>
                          <Box flexDirection="row" alignItems="center" gap={8} marginBottom={4}>
                            <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark}>
                              {coupon.code}
                            </CCText>
                            {isApplied && (
                              <MaterialCommunityIcons name="check-circle" size={18} color={CCColors.Green} />
                            )}
                          </Box>
                          <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                            {coupon.description}
                          </CCText>
                          {coupon.valid_until && (
                            <CCText fontSize={FontSizes[10]} color={CCColors.TextColor.Grey} marginTop={4}>
                              Valid until: {new Date(coupon.valid_until).toLocaleDateString()}
                            </CCText>
                          )}
                        </Box>
                        {!isApplied && (
                          <CCButton
                            onPress={() => handleSelectCoupon(coupon)}
                            style={styles.selectButton}
                            contentContainerStyle={styles.buttonContent}>
                            <CCText fontSize={FontSizes[12]} fontWeight="semibold" color={CCColors.Primary.Default}>
                              Apply
                            </CCText>
                          </CCButton>
                        )}
                      </Box>
                    </Pressable>
                  );
                })}
              </Box>
            </ScrollView>
          ) : (
            <Box padding={20} alignItems="center">
              <MaterialCommunityIcons name="ticket-outline" size={48} color={CCColors.TextColor.Grey} />
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} marginTop={10} textAlign="center">
                No coupons available for this selection
              </CCText>
            </Box>
          )}
        </Box>
      </Box>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderColor: CCColors.Grey.Divider200,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  applyButton: {
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    minWidth: 80,
  },
  selectButton: {
    backgroundColor: CCColors.Primary.lightBrown,
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  buttonContent: {
    padding: 0,
  },
  couponCard: {
    backgroundColor: CCColors.White,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: CCColors.Grey.Divider200,
  },
  couponCardApplied: {
    backgroundColor: CCColors.Primary.lightBrown,
    borderColor: CCColors.Green,
    borderWidth: 2,
  },
});

export default CouponModal;








